/**
 * Admin Authentication Service
 * Handles Firebase email/password and Google OAuth authentication for admin users
 */

import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from 'foodhub-shared/firebase';
import type { Admin, AdminSession } from 'foodhub-shared/types';

const ADMINS_COLLECTION = 'admins';

/**
 * Helper function to get or create admin record after authentication
 */
const getOrCreateAdminRecord = async (user: User): Promise<Admin> => {
  const adminDocRef = doc(db, ADMINS_COLLECTION, user.uid);
  const adminDocSnap = await getDoc(adminDocRef);

  if (adminDocSnap.exists()) {
    const adminData = adminDocSnap.data() as Admin;

    // Check if admin is active
    if (!adminData.active) {
      await signOut(auth);
      throw new Error('Admin account is inactive');
    }

    // Update last login time
    await updateDoc(adminDocRef, {
      lastLoginAt: serverTimestamp(),
    });

    return adminData;
  }

  // For Google Sign-In, create a new admin record if it doesn't exist
  // This allows first-time Google Sign-In users to be created automatically
  const newAdminData: Admin = {
    id: user.uid,
    email: user.email || '',
    name: user.displayName || 'Admin User',
    role: 'admin',
    active: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  await setDoc(adminDocRef, newAdminData);
  return newAdminData;
};

/**
 * Sign in admin with email and password
 */
export const signInAdmin = async (email: string, password: string): Promise<AdminSession> => {
  try {
    // Sign in with Firebase Auth
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get admin data from Firestore
    const adminData = await getOrCreateAdminRecord(user);

    return {
      admin: {
        ...adminData,
        id: user.uid,
      },
      loginTime: new Date(),
    };
  } catch (error: any) {
    console.error('Admin sign in error:', error);
    throw new Error(error.message || 'Failed to sign in');
  }
};

/**
 * Sign in admin with Google
 */
export const signInWithGoogle = async (): Promise<AdminSession> => {
  try {
    const provider = new GoogleAuthProvider();

    // Add custom claims to request
    provider.addScope('profile');
    provider.addScope('email');

    // Sign in with Google
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;

    // Get or create admin record
    const adminData = await getOrCreateAdminRecord(user);

    return {
      admin: {
        ...adminData,
        id: user.uid,
      },
      loginTime: new Date(),
    };
  } catch (error: any) {
    console.error('Google sign in error:', error);

    // Handle specific error cases
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in cancelled');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Sign-in popup was blocked. Please allow popups and try again.');
    }

    throw new Error(error.message || 'Failed to sign in with Google');
  }
};

/**
 * Sign out admin
 */
export const signOutAdmin = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    console.error('Admin sign out error:', error);
    throw new Error(error.message || 'Failed to sign out');
  }
};

/**
 * Get current admin user
 */
export const getCurrentAdmin = async (user: User): Promise<Admin | null> => {
  try {
    const adminDocRef = doc(db, ADMINS_COLLECTION, user.uid);
    const adminDocSnap = await getDoc(adminDocRef);

    if (!adminDocSnap.exists()) {
      return null;
    }

    return {
      ...adminDocSnap.data(),
      id: user.uid,
    } as Admin;
  } catch (error: any) {
    console.error('Get current admin error:', error);
    return null;
  }
};

/**
 * Create admin user (for setup/testing)
 * Note: This should only be called by super admins or during setup
 */
export const createAdminUser = async (
  email: string,
  password: string,
  name: string,
  role: 'super_admin' | 'admin' = 'admin'
): Promise<Admin> => {
  try {
    // Create user in Firebase Auth
    // Note: In production, use Firebase Admin SDK for this
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create admin document in Firestore
    const adminData: Admin = {
      id: user.uid,
      email,
      name,
      role,
      active: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(doc(db, ADMINS_COLLECTION, user.uid), adminData);

    return adminData;
  } catch (error: any) {
    console.error('Create admin user error:', error);
    throw new Error(error.message || 'Failed to create admin user');
  }
};

/**
 * Subscribe to auth state changes
 */
export const subscribeToAuthState = (
  callback: (user: User | null) => void
): (() => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get admin by ID
 */
export const getAdminById = async (adminId: string): Promise<Admin | null> => {
  try {
    const adminDocRef = doc(db, ADMINS_COLLECTION, adminId);
    const adminDocSnap = await getDoc(adminDocRef);

    if (!adminDocSnap.exists()) {
      return null;
    }

    return {
      ...adminDocSnap.data(),
      id: adminId,
    } as Admin;
  } catch (error: any) {
    console.error('Get admin by ID error:', error);
    return null;
  }
};

export default {
  signInAdmin,
  signInWithGoogle,
  signOutAdmin,
  getCurrentAdmin,
  createAdminUser,
  subscribeToAuthState,
  getAdminById,
};

